import { useStore } from '@nanostores/react'
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Modal,
    ModalBody,
    ModalContent,
    Spinner,
    Tooltip,
    useDisclosure,
} from '@nextui-org/react'

import { Alert } from 'beskar/dashboard'
import { useThrowingFn } from 'beskar/landing'
import classNames from 'classnames'
import clsx from 'clsx'
import { PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { atoms } from 'website/src/lib/atoms'
import { githubProjectTypes } from 'website/src/lib/constants'
import { env } from 'website/src/lib/env'
import {
    useAwarenessStates,
    useInviteMemberButton,
    usePrevious,
    useRootProps,
} from 'website/src/lib/hooks'
import { deduplicateByKeyFn } from 'website/src/lib/utils'
import { createInviteLinkAuth } from 'website/src/pages/api/invites'
import { InviteLink } from 'website/src/pages/org/[orgId]/invite'

function useAwarenessUserPageChange() {
    const awareness = useStore(atoms.awareness)

    let previousAwareness = usePrevious(awareness)
    useEffect(() => {
        if (!awareness || !previousAwareness) {
            return
        }
        if (awareness !== previousAwareness) {
            // console.log('removing previous awareness', previousAwareness)
            previousAwareness?.setLocalState(null)
        }
    }, [awareness])
}

export function CurrentUsers() {
    const {
        orgId,
        githubRepo,
        projectType,
        siteId,
        userId,
        hasFreeTrial,
        branchId,
    } = useRootProps()

    let users = useAwarenessStates('user')

    users = deduplicateByKeyFn(users, (x) => x?.email)

    useAwarenessUserPageChange()

    const size = 34
    const {
        modal: inviteModal,
        onInviteClick,
        isLoading,
    } = useInviteMemberButton({
        hasFreeTrial,
        orgId,
        siteId,
    })
    const {
        modal: guestModal,
        isOpen: isGuestOpen,
        onGuestClick,
    } = useInviteGuestButton({
        orgId,
        branchId,
        siteId,
    })
    const showAddUser = !githubProjectTypes.includes(projectType)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const className =
        'appearance-none shrink-0 my-[2px]  flex flex-col items-center justify-center bg-white hover:scale-105 scale-100 transition-all rounded-[10px] ring-2 ring-[--color]'
    return (
        <div className='flex px-1 transition-all duration-300 shrink-0 -space-x-[6px] items-center '>
            {users.map((u) => {
                return (
                    <Tooltip key={u.image || u.name} content={u.name || ''}>
                        <img
                            style={{
                                width: size,
                                height: size,
                                // @ts-ignore
                                '--color': u.color,
                            }}
                            referrerPolicy='no-referrer'
                            className={classNames(className)}
                            src={u.image}
                            alt=''
                        />
                    </Tooltip>
                )
            })}

            {showAddUser && (
                <Dropdown
                    closeOnSelect={false}
                    backdrop='opaque'
                    isOpen={isOpen}
                    onClose={onClose}
                    triggerScaleOnOpen={false}
                >
                    <Tooltip content='Invite Users'>
                        <div className=''>
                            <DropdownTrigger
                                onClick={(e) => {
                                    if (userId) {
                                        return onOpen()
                                    }
                                    e.stopPropagation()
                                    e.preventDefault()
                                    return atoms.showSignupModal.set(true)
                                }}
                            >
                                <button
                                    style={{
                                        width: size,
                                        height: size,
                                    }}
                                    className={classNames(
                                        className,
                                        '!bg-gray-600 text-white group !ring-gray-300',
                                    )}
                                >
                                    <PlusIcon className='' size={18} />
                                </button>
                            </DropdownTrigger>
                        </div>
                    </Tooltip>

                    <DropdownMenu aria-label='invite options'>
                        <DropdownItem
                            onClick={async (e) => {
                                await onInviteClick(e)
                                onClose()
                            }}
                            description='Invite team members with write access'
                            endContent={
                                isLoading && (
                                    <Spinner className='!w-3 mr-2' size='sm' />
                                )
                            }
                            key='member'
                        >
                            Member
                        </DropdownItem>
                        <DropdownItem
                            description='Guests can only suggest changes via Github PRs'
                            onClick={(e) => {
                                onClose()
                                if (!githubRepo) {
                                    return toast.error(
                                        `Connect Github to use guest invites`,
                                    )
                                }
                                onGuestClick(e)
                            }}
                            key='guest'
                        >
                            Guest
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            )}

            {inviteModal}
            {guestModal}
        </div>
    )
}

function useInviteGuestButton({ orgId, branchId = '', siteId = '' }) {
    const guestLink = `${
        env.NEXT_PUBLIC_URL
    }/org/${orgId}/p/${siteId}/b/${encodeURIComponent(branchId)}/suggest`

    const { isOpen, onOpenChange, onOpen } = useDisclosure()

    const onGuestClick = (e) => {
        onOpen()
    }

    return {
        onGuestClick,
        isOpen,

        modal: (
            <Modal
                backdrop='blur'
                isOpen={isOpen}
                size='lg'
                onOpenChange={onOpenChange}
            >
                <ModalContent>
                    <ModalBody>
                        <div className={clsx('', 'p-6 space-y-8')}>
                            <div className='space-y-6'>
                                <div className='font-medium leading-relaxed'>
                                    Send the link below to invite guests to edit
                                    your website, the link doesn't expire
                                </div>

                                <InviteLink inviteLink={guestLink} />
                            </div>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        ),
    }
}
